'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// UI-05 invariant: this component MUST NOT import from memory-suggestion.tsx.
// Visual parallel; structural independence. The two cards share CSS family
// names but no source dependency — verified by grep in TASK-DEV-15 acceptance
// criteria and pinned by a regression test in Plan 05-13.
import { ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCopilot } from './context.js';
import { safeRefHref } from './safe-url.js';
import { useDecideAction } from '../hooks/index.js';
function getSkillSuggestionPreview(args) {
    const d = (args ?? {});
    if (d.kind !== 'skill_suggestion_preview')
        return null;
    if (d.operation !== 'create' && d.operation !== 'update')
        return null;
    if (d.scope !== 'user_track')
        return null;
    return {
        kind: 'skill_suggestion_preview',
        operation: d.operation,
        scope: 'user_track',
        name: typeof d.name === 'string' ? d.name : '',
        description: typeof d.description === 'string' ? d.description : '',
        contentPreview: typeof d.contentPreview === 'string' ? d.contentPreview : '',
        requiredCapabilities: Array.isArray(d.requiredCapabilities) ? d.requiredCapabilities : [],
        optionalCapabilities: Array.isArray(d.optionalCapabilities) ? d.optionalCapabilities : [],
        ...(typeof d.targetSkillId === 'string' ? { targetSkillId: d.targetSkillId } : {}),
    };
}
/**
 * Stable approval decide error-code → user-visible copy map. Extracted from the
 * inline ternary in the card's error-state render so the table can be
 * unit-pinned and reused by render-prop integrators — mirrors `planErrorCopy`
 * (plan-card.tsx) and `scheduleErrorCodeToCopy` (schedule-preview-card.tsx).
 * `code` is the server-driven `errorCode` carried on the cross-message
 * terminal-state part.
 */
export function approvalErrorCodeToCopy(code) {
    switch (code) {
        case 'hash_mismatch':
            return 'This approval no longer matches the proposed action.';
        case 'expired':
            return 'Approval expired.';
        case 'not_found':
            return 'Approval not found.';
        case 'denied':
            return 'You are not allowed to decide this approval.';
        case 'execution_failed':
            return 'The action failed.';
        case 'already_decided':
            // A 409 is a benign no-op, not a failure (PlanCard precedent). Surface an
            // informational chip instead of the generic error copy.
            return 'Already decided.';
        default:
            return 'Something went wrong.';
    }
}
/**
 * Inline approval-gate confirmation card (UI-01, UI-02, UI-05).
 *
 * Surfaces when the agent's tool execution is paused on an approval gate
 * and the runtime emits a `data-approval-pending` SSE part. The card
 * renders the persisted `tf_approval` payload — tool name, summary, args
 * blob, references — with three actions: Approve, Reject (with optional
 * reason textarea per D-10), Edit (freeform-intent textarea pre-filled
 * from the summary per D-08).
 *
 * On Approve: calls `useCopilot().hub.approvalsDecide(...)` with the
 * persisted `payloadHash` (D-01 replay binding), then
 * `useCopilot().observeApprovalResult(approvalId)`: the hub executes and
 * narrates the settled approval by itself (COP-157 D9); the root reloads
 * the session until the hydrated card shows the execution outcome.
 *
 * On Reject: same decide POST with `action: 'reject'`, optional `reason`.
 * Does NOT call `sendMessage` — per D-11 the server appends a synthetic
 * tool-result on the next turn the user types.
 *
 * On Edit: same decide POST with `action: 'edit'` and the freeform
 * intent text as `reason`, then `sendMessage({ text })` so the model
 * re-plans from scratch with the user's edited prompt (D-08).
 *
 * CSS class family: `copilot-approval-card-*` — visual sibling to
 * `copilot-memory-suggestion-card-*` (UI-05 visual parity) with no
 * shared selectors. Owned by `packages/hub-client/src/ui/styles.css`
 * per M6.
 *
 * Citations: UI-01, UI-02, UI-05, COP-011 (hash binding), COP-013
 * (decision audit), COP-024 (admin surface — sibling), COP-051 (memory
 * card; preserved untouched), D-02, D-08, D-10, D-12, D-13, D-18.
 */
export function ApprovalCard({ approval, className, variant = 'default', children, }) {
    const renderProps = useApprovalCard({ approval });
    // Render-prop override — consumers take full control. The bound handlers
    // and state setters are passed so a custom surface still gets free
    // decide + re-attach plumbing.
    if (children) {
        return _jsx(_Fragment, { children: children(renderProps) });
    }
    return _jsx(ApprovalCardDefaultView, { ...renderProps, className: className, variant: variant });
}
/** The most facts the compact card lists before the reader needs the full request. */
const MAX_APPROVAL_FACTS = 6;
/** `estimateId` / `estimate_id` → `Estimate id`. */
function humanizeArgKey(key) {
    const words = key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
/**
 * COP-153 — the compact card's short fact list: the top-level arguments that
 * read as one value (text, number, yes/no), in the order the tool sent them.
 * Nested values stay in the full request, which the card keeps one click away.
 */
export function approvalFacts(args) {
    if (!args || typeof args !== 'object' || Array.isArray(args))
        return [];
    return Object.entries(args)
        .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value) && value !== '')
        .slice(0, MAX_APPROVAL_FACTS)
        .map(([key, value]) => ({
        label: humanizeArgKey(key),
        value: typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value),
    }));
}
/** `Expires 14:16` — a clock time, so the label never goes stale on screen. */
function formatExpiry(expiresAt) {
    if (expiresAt === null)
        return null;
    const at = new Date(expiresAt);
    if (Number.isNaN(at.getTime()))
        return null;
    return `Expires ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
/**
 * Hook candidate for `ApprovalCard` (same-file split, §2). Owns the optimistic
 * `localState` machine, the seeded reject/edit field state, the shared
 * `useDecideAction` busy/error guard, and every decide + re-attach handler,
 * returning the single named `ApprovalCardRenderProps` bag. The handler
 * declarations below are byte-verbatim (their text + the relative order
 * onRejectSubmit → onEditClick → onEditSubmit → onReask are source-grepped by
 * `test/widget-skill-suggestion-approval.test.ts`).
 */
function useApprovalCard({ approval }) {
    const { hub, sendMessage, applyTurnActions, observeApprovalResult } = useCopilot();
    /**
     * COP-142 — hand the decide reply's turn-action snapshot to the runtime.
     *
     * The reply is typed by `ApprovalDecideResponseSchema`, but the widget's `hub`
     * is a structural dispatcher and callers have historically ignored the result,
     * so the field is narrowed defensively rather than trusted: a hub predating
     * COP-142 simply omits it, and an omission must be a NO-OP, never a cleared
     * list. Absent is not empty (COP-135).
     */
    const applyDecidedTurnActions = (result) => {
        const snapshot = result?.turnActions;
        if (snapshot === undefined || snapshot === null)
            return;
        const { turnId, actions } = snapshot;
        if (typeof turnId !== 'string' || !Array.isArray(actions))
            return;
        applyTurnActions({ turnId, actions });
    };
    // Local state mirrors `approval.state` but advances optimistically the
    // instant a button is clicked, so the UI does not look stuck while the
    // decide POST is on the wire and the cross-message terminal SSE part has
    // not yet arrived. On a fresh `approval.state` from the parent (e.g. an
    // SSE-driven terminal-state collector flip), we reconcile.
    const [localState, setLocalState] = useState(approval.state);
    const [reasonText, setReasonText] = useState('');
    const [editText, setEditText] = useState(approval.summary ?? '');
    const [showRejectField, setShowRejectField] = useState(false);
    const [showEditField, setShowEditField] = useState(false);
    // Guards against duplicate clicks. Mirror the memory-suggestion-card
    // pattern (memory-suggestion.tsx line 53). Once true, every action
    // handler is a no-op until a server-driven state reset (`approval.state`
    // flipping to a terminal value handled by the reconcile-effect below).
    // The guard + shared catch-handler live in `useDecideAction`; the terminal
    // error copy for this card is surfaced off `approval.errorCode` in the JSX
    // (server-driven), so `resolveError` only flips state + warns and ignores
    // the caught-error copy.
    const [decideErrorMessage, setDecideErrorMessage] = useState(null);
    const { submittedRef, applyDecideError } = useDecideAction({
        errorCopy: approvalErrorCodeToCopy,
        resolveError: (e) => {
            setLocalState('error');
            setDecideErrorMessage(e.message ?? null);
            if (e.message) {
                // eslint-disable-next-line no-console
                console.warn('[Copilot] approvalsDecide failed:', e.code ?? '', e.message);
            }
        },
    });
    // Reconcile external state (cross-message terminal-state collector in
    // tool-call.tsx, parent re-render). When a terminal state arrives, free
    // the submit guard so the card's "in-flight" chip yields to the terminal
    // state chip cleanly.
    useEffect(() => {
        setLocalState(approval.state);
        if (approval.state === 'approved' ||
            approval.state === 'rejected' ||
            approval.state === 'edited' ||
            approval.state === 'expired' ||
            approval.state === 'error') {
            submittedRef.current = true;
        }
        else if (approval.state === 'pending') {
            submittedRef.current = false;
        }
    }, [approval.state]);
    // COP-093 four-choice consent — approve with an explicit, user-chosen scope.
    // Scope is set ONLY by the button the user clicks, never inferred (COP-044).
    // `per_turn` (the bare Approve) creates no standing grant; `session`/`always`
    // mint the matching grant via the decide route (Plan 03-05 Task 1).
    const approveWithScope = async (consentScope) => {
        if (submittedRef.current)
            return;
        submittedRef.current = true;
        setLocalState('approving');
        try {
            const result = await hub.approvalsDecide({
                params: { id: approval.approvalId },
                body: {
                    action: 'approve',
                    payloadHash: approval.payloadHash,
                    ...(consentScope ? { consentScope } : {}),
                },
            });
            // COP-142 — the approve arm DOES continue the turn, so the live seam will
            // also push this part. Applied here anyway: the two carry the same
            // server-built snapshot, whichever lands second is identical, and leaving
            // one arm out is how the next reader concludes the reply is unreliable.
            applyDecidedTurnActions(result);
            setLocalState('approved');
            // COP-157 D9: the hub executes the approved call and narrates it in a
            // server turn of its own; the browser sends no continuation. The root
            // reloads the session on a backoff ladder until the hydrated card for
            // this approval carries the execution outcome.
            observeApprovalResult(approval.approvalId);
        }
        catch (err) {
            applyDecideError(err);
        }
    };
    const onApprove = () => approveWithScope();
    const onApproveSession = () => approveWithScope('session');
    const onApproveAlways = () => approveWithScope('always');
    const onApproveSchedule = () => approveWithScope('schedule');
    // COP-093 §Revocation — Undo an active always grant. Standing-consent only;
    // it does not decide the approval row, so it does not touch submittedRef /
    // localState transitions. The next same-tool call will card again.
    const onUndoAlways = async () => {
        if (!approval.trackId)
            return;
        try {
            await hub.approvalsRevokeGrant({
                body: { trackId: approval.trackId, toolName: approval.toolName },
            });
        }
        catch (err) {
            // Revoke failure is non-fatal to the card; surface for diagnostics.
            const e = (err ?? {});
            if (e.message) {
                // eslint-disable-next-line no-console
                console.warn('[Copilot] approvalsRevokeGrant failed:', e.code ?? '', e.message);
            }
        }
    };
    const onRejectClick = () => {
        if (submittedRef.current)
            return;
        setShowRejectField(true);
    };
    const closeRejectField = () => setShowRejectField(false);
    const onRejectSubmit = async () => {
        if (submittedRef.current)
            return;
        submittedRef.current = true;
        setLocalState('rejecting');
        const reason = reasonText.trim();
        try {
            const result = await hub.approvalsDecide({
                params: { id: approval.approvalId },
                body: {
                    action: 'reject',
                    payloadHash: approval.payloadHash,
                    ...(reason ? { reason } : {}),
                },
            });
            // COP-142 — THE arm this exists for. Reject continues no turn, so this
            // reply is the ONLY channel: without it the action list keeps reading
            // "Waiting for you" beside a card already reading "Rejected".
            applyDecidedTurnActions(result);
            setLocalState('rejected');
            // D-11: the server appends a synthetic tool-result on the user's
            // next turn. We do NOT trigger sendMessage here — the user will
            // type their next message normally.
        }
        catch (err) {
            applyDecideError(err);
        }
    };
    const onEditClick = () => {
        if (submittedRef.current)
            return;
        setShowEditField(true);
    };
    const closeEditField = () => setShowEditField(false);
    const onEditSubmit = async () => {
        if (submittedRef.current)
            return;
        const trimmed = editText.trim();
        if (!trimmed)
            return;
        submittedRef.current = true;
        setLocalState('editing');
        try {
            const result = await hub.approvalsDecide({
                params: { id: approval.approvalId },
                body: {
                    action: 'edit',
                    payloadHash: approval.payloadHash,
                    reason: trimmed,
                },
            });
            // COP-142 — edit settles the ledger `denied` exactly as reject does
            // (COP-135 D12) and likewise continues no turn.
            applyDecidedTurnActions(result);
            setLocalState('edited');
            // D-08: the edited freeform-intent text becomes a regular user
            // message; the model re-plans from scratch with the updated prompt.
            sendMessage({ text: trimmed });
        }
        catch (err) {
            applyDecideError(err);
        }
    };
    const onReask = () => {
        // Expired branch: kick off a fresh user-typed turn carrying the
        // original summary so the agent can re-propose. Pure sendMessage path
        // (no decide POST — the expired row is final per D-04).
        if (approval.summary) {
            sendMessage({ text: approval.summary });
        }
    };
    return {
        approval,
        state: localState,
        onApprove,
        onRejectClick,
        onRejectSubmit,
        onEditClick,
        onEditSubmit,
        onReask,
        reasonText,
        setReasonText,
        editText,
        setEditText,
        showRejectField,
        showEditField,
        closeRejectField,
        closeEditField,
        onApproveSession,
        onApproveAlways,
        onUndoAlways,
        onApproveSchedule,
        decideErrorMessage,
    };
}
/**
 * Default presentational view for `ApprovalCard` (same-file split, §2).
 * Receives the render-props bag + `className`; the two view-local derivations
 * (`skillPreview`, `argsBlock`) live here. Every `copilot-approval-card-*`
 * class literal lives in this JSX, so the split causes zero `ui/styles.css`
 * churn.
 */
function ApprovalCardDefaultView({ approval, state: localState, className, onApprove, onApproveSession, onApproveAlways, onUndoAlways, onApproveSchedule, decideErrorMessage, onRejectClick, closeRejectField, onEditClick, onEditSubmit, closeEditField, onReask, reasonText, setReasonText, editText, setEditText, showRejectField, showEditField, onRejectSubmit, variant, }) {
    const skillPreview = getSkillSuggestionPreview(approval.args);
    const compact = variant === 'compact';
    const facts = compact && !skillPreview ? approvalFacts(approval.args) : [];
    const expiry = compact ? formatExpiry(approval.expiresAt) : null;
    const argsBlock = skillPreview || approval.args === undefined
        ? ''
        : (() => {
            try {
                return JSON.stringify(approval.args, null, 2);
            }
            catch {
                return String(approval.args);
            }
        })();
    return (_jsxs("section", { className: [
            'copilot-approval-card',
            `copilot-approval-card-state-${localState}`,
            className,
        ]
            .filter(Boolean)
            .join(' '), "data-approval-card": "", "data-approval-id": approval.approvalId, "data-state": localState, role: "region", "aria-label": "Approval required", "data-variant": compact ? 'compact' : undefined, children: [compact && localState === 'pending' && (_jsxs("div", { className: "copilot-approval-card-banner", children: [_jsxs("span", { className: "copilot-approval-card-banner-label", children: [_jsx(ShieldCheck, { className: "copilot-approval-card-banner-icon", "aria-hidden": "true" }), "Needs your approval"] }), expiry && _jsx("span", { className: "copilot-approval-card-banner-expiry", children: expiry })] })), _jsxs("header", { className: "copilot-approval-card-header", children: [_jsx("span", { className: "copilot-approval-card-tool", "data-tool-name": approval.toolName, children: approval.toolName }), approval.summary && (_jsx("p", { className: "copilot-approval-card-summary", children: approval.summary }))] }), skillPreview && (_jsxs("div", { className: "copilot-approval-card-skill-preview", "aria-label": "Proposed personal skill", children: [_jsx("div", { className: "copilot-approval-card-skill-preview-kicker", children: skillPreview.operation === 'update' ? 'Update personal skill' : 'Save personal skill' }), _jsx("h4", { className: "copilot-approval-card-skill-preview-name", children: skillPreview.name }), skillPreview.description && (_jsx("p", { className: "copilot-approval-card-skill-preview-description", children: skillPreview.description })), skillPreview.contentPreview && (_jsx("blockquote", { className: "copilot-approval-card-skill-preview-content", children: skillPreview.contentPreview })), (skillPreview.requiredCapabilities.length > 0 || skillPreview.optionalCapabilities.length > 0) && (_jsxs("dl", { className: "copilot-approval-card-skill-preview-capabilities", children: [skillPreview.requiredCapabilities.length > 0 && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Requires" }), _jsx("dd", { children: skillPreview.requiredCapabilities.join(', ') })] })), skillPreview.optionalCapabilities.length > 0 && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Optional" }), _jsx("dd", { children: skillPreview.optionalCapabilities.join(', ') })] }))] }))] })), facts.length > 0 && (_jsx("dl", { className: "copilot-approval-card-facts", children: facts.map((fact) => (_jsxs("div", { className: "copilot-approval-card-fact", children: [_jsx("dt", { children: fact.label }), _jsx("dd", { children: fact.value })] }, fact.label))) })), argsBlock && !compact && (_jsx("pre", { className: "copilot-approval-card-args", "aria-label": "Action arguments", children: argsBlock })), argsBlock && compact && (_jsxs("details", { className: "copilot-approval-card-request", children: [_jsx("summary", { className: "copilot-approval-card-request-toggle", children: "Show request" }), _jsx("pre", { className: "copilot-approval-card-args", "aria-label": "Action arguments", children: argsBlock })] })), approval.references && approval.references.length > 0 && (_jsx("ul", { className: "copilot-approval-card-references", "aria-label": "References", children: approval.references.map((ref) => {
                    // S17: model-supplied URL — only render as a link when the
                    // scheme allowlist passes; unsafe URLs fall back to the span.
                    const href = safeRefHref(ref.url);
                    return (_jsx("li", { className: "copilot-approval-card-reference", "data-ref-type": ref.type, children: href ? (
                        // D-18: references rendered as deep links into the
                        // originating system (e.g. agreement detail page).
                        _jsx("a", { href: href, target: "_blank", rel: "noreferrer noopener", children: ref.label ?? `${ref.type}:${ref.id}` })) : (_jsx("span", { children: ref.label ?? `${ref.type}:${ref.id}` })) }, `${ref.type}:${ref.id}`));
                }) })), localState === 'pending' &&
                !showRejectField &&
                !showEditField &&
                approval.alreadyAlwaysGranted && (_jsxs("div", { className: "copilot-approval-card-always-granted", role: "note", children: [_jsxs("span", { className: "copilot-approval-card-always-granted-label", children: ["You always allow ", approval.toolName, "."] }), _jsx("button", { type: "button", className: "copilot-approval-card-undo-always", onClick: onUndoAlways, "aria-label": `Stop always allowing ${approval.toolName}`, children: "Undo" })] })), localState === 'pending' &&
                !showRejectField &&
                !showEditField &&
                approval.scheduledExecution === true && (_jsxs("div", { className: "copilot-approval-card-actions copilot-approval-card-consent-choices", children: [_jsx("button", { type: "button", className: "copilot-approval-card-reject", onClick: onRejectClick, "aria-label": "Deny this action", children: "Deny" }), _jsx("button", { type: "button", className: "copilot-approval-card-approve", "data-testid": "approval-approve-once", onClick: onApprove, "aria-label": "Approve this action once", children: "Approve once" }), _jsx("button", { type: "button", className: "copilot-approval-card-approve-schedule", "data-testid": "approval-approve-schedule", onClick: onApproveSchedule, "aria-label": `Approve and allow ${approval.toolName} for this schedule`, children: "Approve and allow for this schedule" }), _jsx("button", { type: "button", className: "copilot-approval-card-edit", onClick: onEditClick, "aria-label": "Edit this action", children: "Edit" })] })), localState === 'pending' &&
                !showRejectField &&
                !showEditField &&
                !approval.alreadyAlwaysGranted &&
                approval.scheduledExecution !== true && (_jsxs("div", { className: "copilot-approval-card-actions copilot-approval-card-consent-choices", children: [_jsx("button", { type: "button", className: "copilot-approval-card-reject", onClick: onRejectClick, "aria-label": "Deny this action", children: "Deny" }), _jsx("button", { type: "button", className: "copilot-approval-card-approve", onClick: onApprove, "aria-label": "Approve this action once", children: "Approve" }), _jsxs("button", { type: "button", className: "copilot-approval-card-approve-session", onClick: onApproveSession, "aria-label": `Allow ${approval.toolName} for the rest of this chat`, children: ["Allow ", approval.toolName, " for the rest of this chat"] }), approval.trackId && (_jsxs("button", { type: "button", className: "copilot-approval-card-approve-always", onClick: onApproveAlways, "aria-label": `Always allow ${approval.toolName}`, children: ["Always allow ", approval.toolName] })), _jsx("button", { type: "button", className: "copilot-approval-card-edit", onClick: onEditClick, "aria-label": "Edit this action", children: "Edit" })] })), showRejectField && (_jsxs("div", { className: "copilot-approval-card-reason", children: [_jsx("label", { htmlFor: `copilot-approval-reject-${approval.approvalId}`, children: "Why? (optional)" }), _jsx("textarea", { id: `copilot-approval-reject-${approval.approvalId}`, value: reasonText, onChange: (e) => setReasonText(e.target.value), maxLength: 2000 }), _jsxs("div", { className: "copilot-approval-card-reason-actions", children: [_jsx("button", { type: "button", className: "copilot-approval-card-reject", onClick: onRejectSubmit, children: "Reject" }), _jsx("button", { type: "button", className: "copilot-approval-card-reason-cancel", onClick: closeRejectField, children: "Cancel" })] })] })), showEditField && (_jsxs("div", { className: "copilot-approval-card-edit-field", children: [_jsx("label", { htmlFor: `copilot-approval-edit-${approval.approvalId}`, children: "Tell the assistant what to change" }), _jsx("textarea", { id: `copilot-approval-edit-${approval.approvalId}`, value: editText, onChange: (e) => setEditText(e.target.value), maxLength: 2000 }), _jsxs("div", { className: "copilot-approval-card-edit-field-actions", children: [_jsx("button", { type: "button", className: "copilot-approval-card-edit", onClick: onEditSubmit, disabled: !editText.trim(), children: "Send" }), _jsx("button", { type: "button", className: "copilot-approval-card-edit-field-cancel", onClick: closeEditField, children: "Cancel" })] })] })), localState === 'approving' && (_jsx("div", { className: "copilot-approval-card-state-approving", role: "status", children: "Submitting approval\u2026" })), localState === 'approved' && (_jsx("div", { className: "copilot-approval-card-state-approved", role: "status", children: "Approved" })), localState === 'approved' && approval.execution === 'executed' && (_jsx("div", { className: "copilot-approval-card-state-executed", role: "status", children: "Applied \u2713" })), localState === 'approved' && approval.execution === 'failed' && (_jsx("div", { className: "copilot-approval-card-state-execution-failed", role: "alert", children: "Execution failed" })), localState === 'approved' && approval.execution === 'unknown' && (_jsx("div", { className: "copilot-approval-card-state-execution-unknown", role: "alert", children: "Unconfirmed \u2014 this may have gone through. Check before running it again." })), localState === 'rejecting' && (_jsx("div", { className: "copilot-approval-card-state-rejecting", role: "status", children: "Rejecting\u2026" })), localState === 'rejected' && (_jsx("div", { className: "copilot-approval-card-state-rejected", role: "status", children: approval.reason ? `Rejected: ${approval.reason}` : 'Rejected' })), localState === 'editing' && (_jsx("div", { className: "copilot-approval-card-state-editing", role: "status", children: "Re-asking the assistant\u2026" })), localState === 'edited' && (_jsx("div", { className: "copilot-approval-card-state-edited", role: "status", children: "Sent. Awaiting new proposal\u2026" })), localState === 'expired' && (_jsxs("div", { className: "copilot-approval-card-state-expired", role: "alert", children: [_jsx("span", { className: "copilot-approval-card-state-expired-label", children: "Approval expired." }), approval.summary && (_jsx("button", { type: "button", className: "copilot-approval-card-reask", onClick: onReask, children: "Ask again" }))] })), localState === 'error' && (_jsxs("div", { className: "copilot-approval-card-state-error", role: "alert", children: [_jsx("span", { className: "copilot-approval-card-state-error-label", children: approvalErrorCodeToCopy(approval.errorCode) }), (approval.errorMessage ?? decideErrorMessage) && (_jsxs("span", { className: "copilot-approval-card-state-error-message", children: [' ', approval.errorMessage ?? decideErrorMessage] }))] }))] }));
}
//# sourceMappingURL=approval-card.js.map